import React from "react";
import { Zap } from "lucide-react";
import { bn, money, amountInWords } from "../lib/format";
import { useSettings } from "../context/SettingsContext";

const NAVY = "#12356E";
const NAVY_D = "#0C264F";
const GREEN = "#17922E";
const GREEN_D = "#0F6E22";
const RED = "#C62828";
const MIN_ROWS = 8;

const MONTHS = ["জানুয়ারি", "ফেব্রুয়ারি", "মার্চ", "এপ্রিল", "মে", "জুন", "জুলাই", "আগস্ট", "সেপ্টেম্বর", "অক্টোবর", "নভেম্বর", "ডিসেম্বর"];
const fmtBnDate = (s?: string | null) => {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${bn(+d)} ${MONTHS[+m - 1]} ${bn(y)}`;
};

function Row({ label, value, bold, color, bg, big }: { label: string; value: string; bold?: boolean; color?: string; bg?: string; big?: boolean }) {
  return (
    <div
      className="flex justify-between items-center px-5"
      style={{ padding: big ? "13px 20px" : "10px 20px", background: bg, color, fontWeight: bold ? 700 : 500, fontSize: big ? 19 : 15.5, borderBottom: "1px solid #E4E8F0" }}
    >
      <span>{label}</span>
      <span className="num">{value}</span>
    </div>
  );
}

export default function MemoTemplate({ memo }: { memo: any }) {
  const { settings } = useSettings();
  const SHOP = settings.shop;
  const M = settings.memo;
  const items = memo.items || [];
  const c = memo.customer || {};
  const totalQty = items.reduce((s: number, it: any) => s + (Number(it.qty) || 0), 0);
  const dueRed = (memo.current_due || 0) > 0;

  return (
    <div className="memo-a4" data-testid="print-memo-a4-container" style={{ fontFamily: "'Tiro Bangla', 'Hind Siliguri', serif", color: "#0f172a" }}>
      {/* top accent strip */}
      <div style={{ height: 8, background: GREEN }} />

      {/* ---------- HEADER (white, 2-colour) ---------- */}
      <div className="flex items-start justify-between px-[11mm] pt-[7mm] pb-[5mm]" style={{ borderBottom: `2px solid ${NAVY}` }}>
        <div className="flex items-center gap-4">
          <div className="w-[24mm] h-[24mm] rounded-2xl grid place-items-center shrink-0 px-1" style={{ background: NAVY, border: `2px solid ${GREEN}` }} data-testid="memo-logo-box">
            <div className="text-center leading-none">
              <Zap size={22} color="#fff" fill={GREEN} strokeWidth={1.5} className="mx-auto" />
              <p className="text-[10.5px] font-extrabold text-white mt-1 leading-[1.15]">{SHOP.name}</p>
            </div>
          </div>
          <div className="min-w-0">
            <h1 className="text-[34px] font-extrabold leading-[1.02]" style={{ color: NAVY }} data-testid="memo-shop-name">{SHOP.name}</h1>
            <p className="text-[10.5px] tracking-[.34em] font-bold mt-0.5" style={{ color: GREEN }}>{SHOP.name_en}</p>
            <p className="text-[12.5px] text-slate-600 mt-1.5 leading-snug">{SHOP.address}</p>
            <p className="text-[12.5px] text-slate-600">স্বত্বাধিকারীঃ {SHOP.proprietor} &nbsp;•&nbsp; মোবাইলঃ {bn(SHOP.mobile)}{SHOP.mobile2 ? `, ${bn(SHOP.mobile2)}` : ""}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <span className="inline-block px-5 py-1.5 rounded-md text-white text-[16px] font-bold" style={{ background: GREEN }}>{M.title}</span>
          <div className="mt-2.5 text-[13.5px] leading-[1.8] text-slate-700">
            <p>নংঃ <b className="num" style={{ color: NAVY }} data-testid="memo-invoice-no">{memo.invoice_no || "—"}</b></p>
            <p>তারিখঃ <b style={{ color: NAVY }}>{fmtBnDate(memo.date)}</b></p>
          </div>
        </div>
      </div>

      {/* ---------- BODY ---------- */}
      <div className="px-[11mm] pt-[5mm] flex-1 flex flex-col">
        {/* Bill To */}
        <div className="flex items-center justify-between px-5 py-3 rounded-lg" style={{ background: "#F1F5FB", border: `1px solid #D3DCEB` }}>
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold tracking-wide" style={{ color: NAVY }}>বিল প্রাপক</p>
            <p className="text-[19px] font-bold mt-0.5 text-slate-900" data-testid="memo-customer-name">{c.name || "নগদ ক্রেতা"}</p>
            <p className="text-[13px] text-slate-600 mt-0.5">
              <span className="text-slate-500">মোবাইলঃ</span> {c.mobile ? bn(c.mobile) : "—"} &nbsp;•&nbsp; <span className="text-slate-500">ঠিকানাঃ</span> {c.address || "—"}
            </p>
          </div>
        </div>

        {/* Items */}
        <table className="w-full mt-4 text-[15px] border-collapse" data-testid="memo-items-table" style={{ border: `1px solid ${NAVY}` }}>
          <thead>
            <tr style={{ background: NAVY, color: "#fff" }}>
              <th className="py-[10px] px-3 w-[12mm] text-center font-semibold">ক্রঃ</th>
              <th className="py-[10px] px-3 text-left font-semibold">পণ্যের বিবরণ</th>
              <th className="py-[10px] px-3 w-[20mm] text-center font-semibold">পরিমাণ</th>
              <th className="py-[10px] px-3 w-[16mm] text-center font-semibold">একক</th>
              <th className="py-[10px] px-3 w-[26mm] text-right font-semibold">দর</th>
              <th className="py-[10px] px-3 w-[28mm] text-right font-semibold">মোট</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it: any, i: number) => (
              <tr key={i} style={{ borderBottom: "1px solid #E8ECF3", background: i % 2 ? "#F7F9FC" : "#fff" }}>
                <td className="py-[8px] px-3 text-center num text-slate-700">{bn(i + 1)}</td>
                <td className="py-[8px] px-3 text-slate-900 font-medium">{it.name}</td>
                <td className="py-[8px] px-3 text-center num text-slate-700">{money(it.qty)}</td>
                <td className="py-[8px] px-3 text-center text-slate-700">{it.unit || "—"}</td>
                <td className="py-[8px] px-3 text-right num text-slate-700">৳ {money(it.rate)}</td>
                <td className="py-[8px] px-3 text-right num text-slate-900 font-semibold">৳ {money(it.total ?? it.qty * it.rate)}</td>
              </tr>
            ))}
            {Array.from({ length: Math.max(0, MIN_ROWS - items.length) }).map((_, i) => (
              <tr key={`p${i}`} style={{ borderBottom: "1px solid #E8ECF3", background: (items.length + i) % 2 ? "#F7F9FC" : "#fff" }}>
                <td className="py-[8px] px-3">&nbsp;</td><td /><td /><td /><td /><td />
              </tr>
            ))}
          </tbody>
        </table>

        {/* words + totals */}
        <div className="grid grid-cols-[1.1fr_1fr] gap-6 mt-4 items-start">
          <div className="space-y-3">
            <div className="rounded-lg px-5 py-3" style={{ background: "#F1FaF2", border: "1px solid #CDE9D3" }} data-testid="memo-amount-in-words">
              <p className="text-[11.5px] font-bold" style={{ color: GREEN_D }}>কথায়</p>
              <p className="text-[15.5px] font-semibold mt-0.5 text-slate-900">{amountInWords(memo.grand_total)}</p>
            </div>
            {memo.note && <div className="rounded-lg px-4 py-2.5 text-[13px] text-slate-700" style={{ background: "#F1F5FB" }}><b>নোটঃ</b> {memo.note}</div>}
            <p className="text-[11px] text-slate-400 leading-relaxed px-1">{M.warranty_note}</p>
          </div>
          <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${NAVY}` }} data-testid="memo-summary-box">
            <Row label={`সাব-টোটাল (${bn(totalQty)} পিস)`} value={`৳ ${money(memo.subtotal)}`} />
            <Row label="ডিসকাউন্ট" value={`- ৳ ${money(memo.discount_amount || 0)}`} />
            <Row label="সর্বমোট" value={`৳ ${money(memo.grand_total)}`} bold big bg={GREEN} color="#fff" />
            {memo.previous_due > 0 && <Row label="পূর্বের বাকি" value={`৳ ${money(memo.previous_due)}`} />}
            <Row label="জমা" value={`৳ ${money(memo.paid || 0)}`} bold color={GREEN_D} />
            <Row label="বাকি" value={`৳ ${money(memo.current_due || 0)}`} bold bg={dueRed ? "#FCEBEB" : "#F1FaF2"} color={dueRed ? RED : GREEN_D} />
          </div>
        </div>

        {/* signatures */}
        <div className="mt-auto pt-[13mm] pb-[6mm] flex justify-between items-end text-[13.5px] text-slate-600">
          <div className="w-[60mm] text-center pt-2" style={{ borderTop: "1px solid #8A97AC" }}>গ্রাহকের স্বাক্ষর</div>
          <div className="w-[60mm] text-center pt-2" style={{ borderTop: "1px solid #8A97AC" }}>{SHOP.name} — কর্তৃপক্ষ</div>
        </div>
      </div>

      {/* footer */}
      <div style={{ background: NAVY_D }} className="px-[11mm] py-[4mm] text-center text-white text-[14px]">
        {M.thank_you} — <span className="font-bold" style={{ color: "#53D16E" }}>{SHOP.name}</span>
      </div>
    </div>
  );
}
